
import React from 'react';
import { Category } from '@/types/assessment';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ChevronDown, Trash, MoveVertical, BadgeDollarSign } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';

interface CategoryCardProps {
  category: Category;
  index: number;
  totalCategories: number;
  onUpdateCategory: (categoryId: string, updates: Partial<Category>) => void;
  onMoveCategory: (currentIndex: number, direction: 'up' | 'down') => void;
  onDeleteCategory?: (categoryId: string) => void;
  isDeleting?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export function CategoryCard({
  category,
  index,
  totalCategories,
  onUpdateCategory,
  onMoveCategory,
  onDeleteCategory,
  isDeleting = false,
  dragHandleProps,
}: CategoryCardProps) {
  // Calculate question counts
  const totalQuestions = category.questions.length;
  const premiumQuestions = category.questions.filter(q => q.premium).length;
  const freeQuestions = totalQuestions - premiumQuestions;

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteCategory && window.confirm('Are you sure you want to delete this category?')) {
      onDeleteCategory(category.id);
    }
  };

  return (
    <Collapsible key={category.id} className="border rounded-lg">
      <div className="flex w-full items-center justify-between p-4 hover:bg-accent rounded-t-lg">
        <CollapsibleTrigger className="flex items-center gap-2 font-medium text-left flex-1">
          <div 
            className="cursor-move p-1 rounded hover:bg-gray-100 touch-none" 
            {...dragHandleProps}
          >
            <MoveVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          {category.name || 'Unnamed Category'}
          
          <div className="flex items-center gap-1.5 ml-2">
            <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200">
              {totalQuestions} Questions
            </Badge>
            {freeQuestions > 0 && (
              <Badge variant="outline" className="text-xs bg-[#F1F0FB] border-slate-200">
                {freeQuestions} Free
              </Badge>
            )}
            {premiumQuestions > 0 && (
              <Badge variant="outline" className="text-xs bg-[#F1F0FB] border-slate-200 flex items-center gap-1">
                <BadgeDollarSign className="h-3 w-3 text-[#9b87f5]" />
                {premiumQuestions}
              </Badge>
            )}
          </div>
        </CollapsibleTrigger>
        
        <div className="flex items-center gap-2 ml-2">
          {onDeleteCategory && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleDeleteClick}
              disabled={isDeleting}
            >
              <Trash className="h-4 w-4" />
            </Button>
          )}
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ChevronDown className="h-4 w-4" />
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
      
      <CollapsibleContent>
        <div className="p-4 border-t space-y-4">
          <div className="space-y-2">
            <Label>Category Name</Label>
            <Input
              value={category.name}
              onChange={(e) => onUpdateCategory(category.id, { name: e.target.value })}
              placeholder="Enter category name"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={category.description}
              onChange={(e) => onUpdateCategory(category.id, { description: e.target.value })}
              placeholder="Enter category description"
              rows={3}
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
